<template>
  <div class="token-sidebar">
    <div class="token-sidebar__header">
      <div class="mb-2 d-flex align-items-center">
        <img src="@/assets/img/editor-token.svg" style="height: 16px;" />
        <span class="ml-2 font-16 font-weight-bold">Selecciona Token</span>
      </div>
      <div class="font-14 color-secondary-6 mb-3 mt-1">
        Selecciona o escribe el nombre del token correspondiente.
      </div>
      <div class="mb-4">
        <div class="font-14 font-weight-bold mb-1">Búsqueda de Tokens</div>
        <div class="input-icon">
          <i class="fas fa-search color-orange" />
          <input
            v-model="search"
            class="form-control form-control-sm"
            placeholder="Escriba Nombre del Token"
            type="text"
          />
        </div>
      </div>
    </div>
    <div class="token-sidebar__list">
      <div v-if="reservedTokensToShow.length" class="reserved-tokens mb-3">
        <div class="section-title">Tokens Reservados</div>
        <div
          v-for="token in reservedTokensToShow"
          :key="'reserved-' + token.name"
          class="token-item"
          @click="$emit('insert-token', token)"
        >
          <span class="token-name">{{ token.name }}</span>
          <span class="token-type token-type--is-datetime">
            {{ token.dataType }}
          </span>
        </div>
      </div>
      <div class="custom-tokens">
        <div class="section-title">Tokens Personalizados</div>

        <div
          v-for="group in groupedTokens"
          :key="group.name || 'ungrouped'"
          class="token-group"
        >
          <div v-if="group.name" class="section-title mb-2">
            {{ group.name }}
          </div>
          <div
            v-for="token in group.items"
            :key="token.id || token.name"
            class="token-item"
            @click="$emit('insert-token', token)"
          >
            <span class="token-name">{{ token.name }}</span>
            <span
              class="token-type"
              :class="{
                'token-type--is-string': isTokenString(token),
                'token-type--is-number': isTokenNumber(token),
                'token-type--is-boolean': isTokenBoolean(token),
                'token-type--is-datetime': isTokenDateTime(token),
                'token-type--is-conditional': isTokenConditional(token),
                'token-type--is-loop': isTokenLoop(token),
              }"
            >
              {{ token.dataType || 'N/A' }}
            </span>
          </div>
        </div>
      </div>

      <div
        v-if="!filteredTokens.length && !reservedTokensToShow.length"
        class="token-empty"
      >
        No se encontraron tokens.
      </div>
    </div>
  </div>
</template>

<script>
import restrictedTokens from '@/utils/constants/contractEditorRestrictedTokens';
export default {
  name: 'TokenSidebar',
  props: {
    tokens: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    search: '',
    reservedTokens: [
      { name: '_fecha', dataType: 'datetime' },
      { name: '_fecha_hora', dataType: 'datetime' },
      { name: '_numero_pagina', dataType: 'number' },
      { name: '_total_pagina', dataType: 'number' },
      {
        name: '_if',
        dataType: 'conditional',
        template: '{{#if <condicion>}}\n\n{{#endif}}',
      },
      {
        name: '_lista',
        dataType: 'loop',
        template: '{{#each <lista>}}\n  {{campo}}\n{{#endeach}}',
      },
    ],
  }),
  computed: {
    filteredTokens() {
      if (!this.search.trim()) return this.tokens;
      const term = this.search.toLowerCase();
      return this.tokens.filter(
        (t) =>
          t.name.toLowerCase().includes(term) ||
          (t.dataType && t.dataType.toLowerCase().includes(term))
      );
    },
    reservedTokensToShow() {
      return this.reservedTokens;
    },
    isTokenString() {
      return (t) => t.dataType.toLowerCase() === 'string';
    },
    isTokenNumber() {
      return (t) => t.dataType.toLowerCase() === 'number';
    },
    isTokenBoolean() {
      return (t) => t.dataType.toLowerCase() === 'boolean';
    },
    isTokenDateTime() {
      return (t) => t.dataType.toLowerCase() === 'datetime';
    },
    isTokenConditional() {
      return (t) => t.dataType.toLowerCase() === 'conditional';
    },
    isTokenLoop() {
      return (t) => t.dataType.toLowerCase() === 'list';
    },
    groupedTokens() {
      const groups = [];
      const listTokens = Object.keys(restrictedTokens);
      const ungrouped = this.tokens.filter((t) => {
        const nameLower = t.name.trim().toLowerCase();
        const isSubToken = Object.values(restrictedTokens).some((subTokens) =>
          subTokens.some((st) => st.trim().toLowerCase() === nameLower)
        );
        const isParentList = listTokens.some(
          (lt) => lt.trim().toLowerCase() === nameLower
        );
        return !isSubToken && !isParentList;
      });
      if (ungrouped.length) {
        groups.push({
          name: '',
          items: ungrouped,
        });
      }
      for (const [groupName, subTokens] of Object.entries(restrictedTokens)) {
        const items = this.tokens.filter((t) =>
          subTokens.some(
            (st) => st.trim().toLowerCase() === t.name.trim().toLowerCase()
          )
        );
        if (items.length) {
          const parentToken = { name: groupName, dataType: 'list' };
          groups.push({
            name: groupName,
            items: [parentToken, ...items],
          });
        }
      }
      if (this.search.trim()) {
        const term = this.search.toLowerCase();
        return groups
          .map((g) => ({
            name: g.name,
            items: g.items.filter(
              (t) =>
                t.name.toLowerCase().includes(term) ||
                (t.dataType && t.dataType.toLowerCase().includes(term))
            ),
          }))
          .filter((g) => g.items.length);
      }
      return groups;
    },
  },
};
</script>

<style lang="scss" scoped>
.token-sidebar {
  width: 350px;
  background: #fff;
  box-shadow: 0 10px 30px 0 rgba(0, 0, 0, 0.07);
  position: sticky;
  top: 20px;
  height: calc(100vh - 40px);
  overflow-y: auto;
  padding: 15px;

  .input-icon {
    position: relative;
  }
  .input-icon i {
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: #9e9e9e;
    font-size: 12px;
  }
  .input-icon input {
    padding-left: 32px;
  }
  &__header {
    margin-bottom: 15px;

    h6 {
      color: #2d3748;
      font-weight: 600;

      i {
        color: #4299e1;
      }
    }

    .form-control {
      width: 100%;
      padding: 8px;
      font-size: 0.9rem;
      border: 1px solid #e2e2e2;
      border-radius: 4px;
      transition: border-color 0.2s ease;
      height: 39px;

      &:focus {
        border-color: #4299e1;
        outline: none;
        box-shadow: 0 0 0 2px rgba(66, 153, 225, 0.2);
      }
    }
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 10px;

    .reserved-tokens,
    .custom-tokens {
      .section-title {
        font-weight: bold;
        font-size: 14px;
      }
    }
  }

  .token-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 12px;
    border: 1px solid #e2e2e2;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s ease;
    margin-bottom: 5px;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }

    .token-name {
      font-weight: 500;
      color: #2d3748;
      font-size: 14px;
      padding-right: 10px;
    }

    .token-type {
      font-size: 12px;
      padding: 3px 8px;
      border-radius: 4px;
      font-size: 12px;
      text-transform: uppercase;
      &--is-string {
        border: 1px solid #f3af54;
        background: rgba(243, 175, 84, 0.05);
        color: #f3af54;
      }
      &--is-number {
        border: 1px solid #2db782;
        background: #f4fbf9;
        color: #2db782;
      }
      &--is-boolean {
        border: 1px solid #2980b9;
        background: rgba(41, 128, 185, 0.08);
        color: #2980b9;
      }
      &--is-datetime {
        border: 1px solid #6154f3;
        background: rgba(97, 84, 243, 0.05);
        color: #6154f3;
      }
      &--is-conditional {
        border: 1px solid #ff6b6b;
        background: rgba(255, 107, 107, 0.05);
        color: #ff6b6b;
      }
      &--is-loop {
        border: 1px solid #9b59b6;
        background: rgba(155, 89, 182, 0.08);
        color: #9b59b6;
      }
    }
  }

  .token-empty {
    text-align: center;
    color: #718096;
    font-size: 0.9rem;
    padding: 20px 10px;
  }
}

.token-group {
  padding-top: 8px;
}

.token-group:first-of-type {
  border-top: none;
}

@media (max-width: 992px) {
  .token-sidebar {
    width: 100%;
    position: static;
    height: auto;
    max-height: 300px;
  }
}
</style>
